import React from 'react';

/**
 * Faint animated trade scene used behind the company header.
 *
 * A single inline SVG — no image requests — showing the commodity trade chain:
 * a refinery skyline, port cranes and stacked containers, sugar silos, and a
 * cargo vessel drifting across a calm sea.
 *
 * Deliberately low-contrast (a few percent opacity) so it reads as texture and
 * never competes with the company name or the About copy sitting on top of it.
 * All motion is disabled under prefers-reduced-motion.
 */
const TradeSceneBackdrop = ({ tint = '#2B4B6A' }) => (
  <div className="tsb" aria-hidden="true">
    <style>{`
      .tsb {
        position: absolute; inset: 0; overflow: hidden; pointer-events: none;
        border-radius: inherit; z-index: 0;
      }
      /* Everything else in the host container paints above the scene, so the
         company name and About copy are never sitting behind artwork. */
      .tsb-host > *:not(.tsb) { position: relative; z-index: 1; }
      .tsb svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }
      .tsb__sky { opacity: .05; }
      .tsb__far { opacity: .07; }
      .tsb__near { opacity: .10; }
      .tsb__sea { opacity: .08; }

      /* vessel crosses slowly, then loops */
      .tsb__ship { animation: tsb-sail 46s linear infinite; }
      /* second vessel, offset so the scene never looks empty */
      .tsb__ship2 { animation: tsb-sail 68s linear infinite; animation-delay: -22s; }
      .tsb__wave { animation: tsb-swell 7s ease-in-out infinite; }
      .tsb__wave--b { animation-duration: 9.5s; animation-delay: -3s; }
      .tsb__smoke { animation: tsb-rise 6s ease-in-out infinite; transform-origin: center bottom; }
      .tsb__smoke--b { animation-delay: -2.5s; animation-duration: 7.5s; }
      .tsb__smoke--c { animation-delay: -4s;   animation-duration: 8.5s; }
      .tsb__crane { animation: tsb-nudge 11s ease-in-out infinite; transform-origin: 50% 100%; }

      @keyframes tsb-sail {
        from { transform: translateX(-28%); }
        to   { transform: translateX(128%); }
      }
      @keyframes tsb-swell {
        0%, 100% { transform: translateX(0); }
        50%      { transform: translateX(-14px); }
      }
      @keyframes tsb-rise {
        0%   { opacity: 0;   transform: translateY(4px) scale(.85); }
        35%  { opacity: .85; }
        100% { opacity: 0;   transform: translateY(-16px) scale(1.35); }
      }
      @keyframes tsb-nudge {
        0%, 100% { transform: rotate(0deg); }
        50%      { transform: rotate(-1.4deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .tsb__ship, .tsb__ship2, .tsb__wave, .tsb__smoke, .tsb__crane { animation: none; }
        .tsb__smoke { opacity: .5; }
      }
    `}</style>

    <svg viewBox="0 0 1200 300" preserveAspectRatio="xMidYMax slice" xmlns="http://www.w3.org/2000/svg">
      {/* ---------- far skyline: refinery + storage tanks ---------- */}
      <g className="tsb__far" fill={tint}>
        {/* distillation columns */}
        <rect x="52" y="132" width="13" height="96" rx="2" />
        <rect x="72" y="112" width="17" height="116" rx="2" />
        <rect x="96" y="146" width="11" height="82" rx="2" />
        {/* flare stack */}
        <rect x="118" y="98" width="6" height="130" />
        <path d="M121 98c6-9 2-15 0-20-3 6-9 10-3 20z" opacity=".8" />
        {/* pipe rack */}
        <rect x="46" y="204" width="86" height="4" />
        <rect x="46" y="214" width="86" height="3" />
        {/* spherical + cylindrical storage */}
        <circle cx="156" cy="206" r="20" />
        <rect x="186" y="182" width="34" height="46" rx="3" />
        <rect x="226" y="192" width="28" height="36" rx="3" />
      </g>

      {/* smoke drifting off the refinery */}
      <g className="tsb__far" fill={tint}>
        <circle className="tsb__smoke" cx="80" cy="106" r="7" />
        <circle className="tsb__smoke tsb__smoke--b" cx="121" cy="92" r="5" />
        <circle className="tsb__smoke tsb__smoke--c" cx="59" cy="126" r="6" />
      </g>

      {/* ---------- sugar silos + warehouse ---------- */}
      <g className="tsb__far" fill={tint}>
        <path d="M980 228v-52a16 16 0 0 1 32 0v52z" />
        <path d="M1020 228v-44a14 14 0 0 1 28 0v44z" />
        <path d="M1056 228v-58a17 17 0 0 1 34 0v58z" />
        {/* warehouse with a pitched roof */}
        <path d="M1098 228v-34l30-16 30 16v34z" />
        <rect x="1118" y="206" width="20" height="22" opacity=".55" />
        {/* conveyor from silos to warehouse */}
        <rect x="1044" y="182" width="60" height="3" transform="rotate(-7 1044 182)" />
      </g>

      {/* ---------- port cranes ---------- */}
      <g className="tsb__near" fill={tint}>
        <g className="tsb__crane">
          <rect x="330" y="120" width="4" height="108" />
          <rect x="366" y="120" width="4" height="108" />
          <rect x="318" y="116" width="96" height="5" rx="2" />
          <rect x="404" y="116" width="52" height="4" rx="2" />
          <rect x="348" y="121" width="3" height="30" />
          <rect x="341" y="149" width="17" height="12" rx="2" />
        </g>
        <g className="tsb__crane" style={{ animationDelay: '-5s' }}>
          <rect x="470" y="134" width="4" height="94" />
          <rect x="502" y="134" width="4" height="94" />
          <rect x="458" y="130" width="86" height="5" rx="2" />
          <rect x="536" y="130" width="44" height="4" rx="2" />
          <rect x="486" y="135" width="3" height="24" />
          <rect x="479" y="157" width="17" height="12" rx="2" />
        </g>
      </g>

      {/* ---------- stacked containers on the quay ---------- */}
      <g className="tsb__near" fill={tint}>
        {[
          [330, 196], [366, 196], [402, 196], [438, 196], [474, 196], [510, 196],
          [348, 178], [384, 178], [420, 178], [456, 178], [492, 178],
          [366, 160], [402, 160], [438, 160],
          [384, 142],
        ].map(([x, y], i) => (
          <rect key={`c-${x}-${y}`} x={x} y={y} width="34" height="16" rx="2"
                opacity={0.55 + ((i % 4) * 0.12)} />
        ))}
        {/* quay edge */}
        <rect x="300" y="212" width="300" height="6" />
      </g>

      {/* ---------- sea ---------- */}
      <g className="tsb__sea" fill={tint}>
        <path className="tsb__wave"
              d="M-40 246c60 0 60-9 120-9s60 9 120 9 60-9 120-9 60 9 120 9 60-9 120-9 60 9 120 9 60-9 120-9 60 9 120 9 60-9 120-9 60 9 120 9v70H-40z" />
        <path className="tsb__wave tsb__wave--b" opacity=".6"
              d="M-40 262c70 0 70-8 140-8s70 8 140 8 70-8 140-8 70 8 140 8 70-8 140-8 70 8 140 8 70-8 140-8 70 8 140 8v60H-40z" />
      </g>

      {/* ---------- vessels ---------- */}
      <g className="tsb__ship tsb__near" fill={tint}>
        <g transform="translate(0 210)">
          {/* hull */}
          <path d="M0 18h176l-14 18H14z" />
          {/* deck containers */}
          <rect x="24" y="4" width="26" height="13" rx="1.5" opacity=".8" />
          <rect x="54" y="4" width="26" height="13" rx="1.5" opacity=".65" />
          <rect x="84" y="4" width="26" height="13" rx="1.5" opacity=".8" />
          <rect x="34" y="-9" width="26" height="12" rx="1.5" opacity=".7" />
          <rect x="64" y="-9" width="26" height="12" rx="1.5" opacity=".55" />
          {/* bridge + funnel */}
          <rect x="126" y="-2" width="26" height="19" rx="2" />
          <rect x="136" y="-14" width="8" height="13" rx="1.5" />
        </g>
      </g>

      <g className="tsb__ship2 tsb__far" fill={tint}>
        <g transform="translate(0 232) scale(.62)">
          <path d="M0 18h176l-14 18H14z" />
          <rect x="30" y="4" width="26" height="13" rx="1.5" opacity=".7" />
          <rect x="62" y="4" width="26" height="13" rx="1.5" opacity=".6" />
          <rect x="94" y="4" width="26" height="13" rx="1.5" opacity=".7" />
          <rect x="128" y="-2" width="26" height="19" rx="2" />
          <rect x="138" y="-14" width="8" height="13" rx="1.5" />
        </g>
      </g>
    </svg>
  </div>
);

export default TradeSceneBackdrop;
